"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { init } from "@/shared/auth/session-controller";
import { useSessionStatus } from "@/shared/hooks/use-session-status";
import { postLoginTarget } from "../post-login-target";

/**
 * A signed-in person never sees the sign-in form.
 *
 * proxy.ts forwards /login and /signup to /app on cookie PRESENCE, but it
 * deliberately stands down when `?redirect` is set (that is the shape
 * forceSignOut produces), and it cannot tell a lapsed access cookie from a
 * live session. This component is the in-tree half: it boots the session
 * controller (AuthProvider is scoped to the app subtrees, so nothing else
 * does on these pages) and, the moment the controller resolves to
 * `authenticated` — directly from /me, or after the boot path rotated a
 * lapsed access token through the refresh door — replaces the page with
 * the post-login target. It renders nothing, reads the URL only inside the
 * effect, and so leaves the page statically prerenderable.
 *
 * `unauthenticated` and `loading` change nothing: the form stays, which is
 * exactly right after a real sign-out.
 */
export function AuthenticatedRedirect() {
  const status = useSessionStatus();
  const router = useRouter();

  useEffect(() => {
    void init();
  }, []);

  useEffect(() => {
    if (status !== "authenticated") return;
    router.replace(postLoginTarget(window.location.search));
  }, [status, router]);

  return null;
}
